import React from "react";
import axios from "axios";
import moment from "moment";
import ReactTooltip from "react-tooltip";
import Dialog from "rc-dialog";

import Loader from "../../components/Loader";
import FormErrorHandler from "../../components/FormErrorHandler";
import CertifyStudentsTrainerVersionHistorySection from "./components/CertifyStudentsTrainerVersionHistorySection";
import PassRequestModal from "./components/PassRequestModal";
import { scrollToElem } from "../../functions";
import { buildCertificationTree, CertificateType, filterUnrelatedCertifications, isRootCertificationNode, removeSiblingsOfProfile } from "../../utils/certifications";
import { hasStaffRole, isTrainerContact } from "../../utils/auth";


class CertifyStudentsTrainerVersion extends React.Component {
    constructor(props) {
        super(props);

        this.fallbackCloseDialog = this?.props?.fallbackCloseDialog && typeof this.props.fallbackCloseDialog === "function" ? this.props.fallbackCloseDialog : () => true;

        this.state = {
            students: [],
            loading: false,
            certificationHistorySectionOpen: false,
            submitting: false,
            isPolling: false,
            message: "",
            passModalOpen: false,
            certifications: [],
            levelTwoPassWarningVisible: false
        };

        this.pollingInterval = null;
    }

    componentDidMount() {
        this.fetchStudents();
        this.pollCalculatedResults();
        this.pollingInterval = setInterval(this.pollCalculatedResults, 5000);
        this.fetchCertifications();
    }

    componentWillUnmount() {
        clearInterval(this.pollingInterval);
    }

    pollCalculatedResults = async () => {
        try {
            const { data } = await axios.get(`/v1/courses/${this.props.params?.itemID}/calculation_state/`);

            if (!data?.ready) {
                this.setState({
                    isPolling: true,
                    message: data.message
                });
            } else {
                this.setState({
                    isPolling: false,
                    message: data.message
                });

                clearInterval(this.pollingInterval);
                this.fetchStudents();
            }
        } catch (error) {
            console.error("Error while polling:", error);
            clearInterval(this.pollingInterval);
        }
    };

    handleChangeStudentField = (e, uuid, field) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;

        this.setState((state) => {
            const students = state.students.map((student) => {
                if (student.uuid === uuid) {
                    return { ...student, [field]: value };
                }
                return student;
            });

            return { students };
        });
    };

    checkAllStudentsForField = (field) => {
        const allChecked = this.state.students.every((s) => s[field] === true);

        this.setState((state) => {
            const students = state.students.map((student) => {
                const hasNoCertificate = !this.props.certificates.some((cert) => cert.user.uuid === student.uuid);

                if (field === "checkedForSubmit") {
                    return {
                        ...student,
                        [field]: hasNoCertificate ? (allChecked ? false : true) : student[field]
                    };
                } else {
                    return {
                        ...student,
                        [field]: allChecked ? false : true
                    };
                }
            });

            return { students };
        });
    };

    handleSubmit = () => {
        this.setState({ submitting: true }, async () => {
            try {
                const studentsBeingCertified = this.state.students
                    .filter((s) => s.checkedForSubmit && s.selectedCertification && s.selectedCertification.length > 0)
                    .map((s) => s.uuid);

                const postData = this.state.students
                    .filter((s) => s.checkedForSubmit && s.selectedCertification && s.selectedCertification.length > 0)
                    .flatMap((student) =>
                        student.selectedCertification.map((certification, index) => ({
                            user: student.uuid,
                            course: this.props.course.uuid,
                            competence_profile: certification.value,
                            physical_pass: index === 0 ? student.passChecked : false
                        }))
                    );

                await axios.post("/v1/certifications/", postData);

                const selectedStudents = this.state.students.filter((s) => s.checkedForSubmit && s.selectedCertification && s.selectedCertification.length > 0);

                if (selectedStudents.some((student) => student.selectedInsured || student.selectedEmployerSpecific)) {
                    const combinedStudentOptions = selectedStudents.map((student) => {
                        return {
                            user: student.uuid,
                            ...(!!student.selectedInsured && { insured: true }),
                            ...(!!student.selectedEmployerSpecific && { employer_specific: true })
                        };
                    });

                    const transformedData = {
                        student_options: combinedStudentOptions
                    };

                    await axios.patch("/v1/courses/" + this.props.params?.itemID + "/", transformedData);
                }

                this.setState({ submitting: false });

                this.props.addGlobalMessage({
                    title: "Certificaten zijn aangemaakt voor de studenten die u heeft geselecteerd."
                });

                this.props.refreshStudents();
                this.props.courseDetailMutate();
                this.props.fallbackCloseDialog();
            } catch (error) {
                console.log(error);
                let errorMessage = error?.response?.data;
                this.setState({
                    submitErrors: errorMessage,
                    // errorMessage: Array.isArray(errorMessage) ? errorMessage[0] : errorMessage,
                    submitting: false
                });
                scrollToElem(document.querySelector(".form-error"));
            }
        });
    };

    findHighestCertification = (certifications, level = 1) => {
        let highestCertification = [];

        // Check for available certifications at the current level
        for (const certification of certifications) {
            if (certification.available) {
                highestCertification.push({ ...certification, level });
            }
        }

        // If any certifications at the current level are available, return them
        if (highestCertification.length > 0) {
            return highestCertification;
        }

        // Otherwise, go deeper
        for (const certification of certifications) {
            if (certification.children && certification.children.length > 0) {
                highestCertification = highestCertification.concat(this.findHighestCertification(certification.children, level + 1));
            }
        }

        return highestCertification;
    };

    fetchStudents = () => {
        this.setState({ loading: true }, async () => {
            const hasOldCertification = (certification) => {
                // Johan: NIBHV-522: No longer checking this ourselves, if people want
                // to do a course every day, they should be free to do so.
                // If a certification can't be requested, the server won't make it available.
                return false;
                // if (!certification.competence_profile.current_certification) return false;
                //
                // const end = moment();
                // const start = moment().subtract(93, "days");
                // const date = moment(certification.competence_profile.current_certification.date);
                //
                // const state = date.isBetween(start, end);
                // return state;
            };
            try {
                const res = await axios.get(`/v1/courses/${this.props.params?.itemID}/students/?limit=10000`);

                const students = res.data.results
                    .filter((s) => !s.cancelled)
                    .filter((st) => {
                        const certifications = filterUnrelatedCertifications(this.props.competenceProfile, st.available_certifications);

                        const hasAvailableCertification = certifications.some((certification) => certification.available);
                        const hasCertificateCreated = this.props.certificates.some((cert) => cert.user.uuid === st.uuid);

                        return hasAvailableCertification || hasCertificateCreated;
                    })
                    .map((student) => {
                        const hasCertificate = this.props.certificates.some((cert) => cert.user.uuid === student.uuid);

                        const relatedOnly = filterUnrelatedCertifications(this.props.competenceProfile, student.available_certifications);
                        const tree = buildCertificationTree(relatedOnly);
                        const withoutSiblings = removeSiblingsOfProfile(this.props.competenceProfile, tree);

                        const highestCertification = this.findHighestCertification(withoutSiblings);

                        const findCPNode = (nodes, uuid) => {
                            for (const node of nodes) {
                                if (node.competence_profile.uuid === uuid) {
                                    return node;
                                }
                                if (node.children) {
                                    const child = findCPNode(node.children, uuid);
                                    if (child) {
                                        return child;
                                    }
                                }
                            }
                            return null;
                        };

                        let selectedCertification = [];

                        if (highestCertification?.length > 0) {
                            // When the highest certification is a root certification,
                            // we want to show the children of the highest certification,
                            // else we only allow until the current depth.
                            const highestCertDepth = Math.min(...highestCertification.map((c) => c.depth))
                            const depths = highestCertDepth === 1 ? [1, 2] : [1, highestCertDepth];

                            selectedCertification = [
                                ...(highestCertification.some((c) => depths.includes(c.level) && !hasOldCertification(c)) ? highestCertification : []),
                                ...(highestCertification.length === 1 &&
                                isRootCertificationNode(highestCertification[0]) &&
                                !highestCertification[0].competence_profile.repetition
                                    ? highestCertification[0].children.filter((c) => c.available)
                                    : [])
                            ].map((c) => ({
                                certificate_type: c.competence_profile.certificate_type,
                                parents: c.competence_profile.parents,
                                label: c.competence_profile.name,
                                value: c.competence_profile.uuid
                            }));
                        }

                        const completeCertifications = filterUnrelatedCertifications(this.props.competenceProfile, student.available_certifications).filter(
                            isRootCertificationNode
                        );

                        if (
                            completeCertifications.every(
                                (certification) =>
                                    certification.reasons?.some((reason) => reason.type === "already_certified") ||
                                    (certification.current_certification?.current_course === false &&
                                        new Date(certification.current_certification?.expiration_date) < new Date())
                            )
                        ) {
                            selectedCertification = [];
                        }

                        return {
                            ...student,
                            checkedForSubmit: !hasCertificate,
                            passChecked: false,
                            selectedEmployerSpecific: student.student_options.employer_specific || false,
                            selectedInsurance: student.student_options.insured || false,
                            selectedCertification
                        };
                    });

                this.setState({
                    students,
                    loading: false
                });
            } catch (error) {
                this.setState({ loading: false });
                console.error("Error fetching students:", error);
            }
        });
    };

    renderStudentCertifications = (availableCertifications, selectedCertification, uuid) => {
        const certificationTree = buildCertificationTree(availableCertifications);
        return this.renderCertificationNodes(certificationTree, selectedCertification, uuid);
    };

    renderCertificationNodes = (certifications, selectedCertification, uuid, isChild = false, depth = 1, noIndent, showTooltip = true) => {
        const sortedCertifications = [...certifications].sort((a, b) => (a.available && !b.available ? -1 : 1));
        const filteredCertifications = filterUnrelatedCertifications(this.props.competenceProfile, sortedCertifications);

        return filteredCertifications.map((certification) => {
            const isChecked = selectedCertification?.some((option) => option.value === certification.competence_profile.uuid);

            const isGeneratedInAnotherCourse =
                certification.reasons?.some((reason) => reason.type === "already_certified") ||
                (certification.current_certification?.current_course === false && new Date(certification.current_certification?.expiration_date) < new Date());

            const { language, repetition, tags } = certification.competence_profile;
            const nameVar = !!certification.competence_profile.certification_name
                ? certification.competence_profile.certification_name
                : certification.competence_profile.name;

            return isChecked || isGeneratedInAnotherCourse ? (
                <div key={certification.competence_profile.uuid} style={{ marginLeft: isChild && !noIndent ? "20px" : "0" }}>
                    <label
                        htmlFor={`student-option-${uuid}-${certification.competence_profile.uuid}`}
                        className="certification-name"
                        style={{ fontWeight: isChild ? "500" : "700", color: isChecked ? "green" : "" }}
                        title={`${language.toUpperCase()}, ${repetition ? "herhaling" : "geen herhaling"}, [${tags.join(", ")}]`}
                    >
                        <CertificateType type={certification.competence_profile.certificate_type} /> - {nameVar}
                    </label>
                    <label htmlFor={`student-option-${uuid}-${certification.competence_profile.uuid}`}>
                        {isGeneratedInAnotherCourse && (
                            <>
                                <i data-tip data-for="tip-gray-check" className="fa fa-check" style={{ color: "#999" }}></i>
                                <ReactTooltip html={true} id="tip-gray-check" effect="solid" place="left">
                                    (deel)certificering al aangemaakt
                                </ReactTooltip>
                            </>
                        )}
                    </label>
                    {showTooltip !== false && (
                        <>
                            <i
                                style={{ marginTop: "5px", width: "25px", height: "25px", color: "#036" }}
                                className="fa fa-info-circle"
                                data-tip
                                data-for={`tip-${uuid}-${certification.competence_profile.uuid}-availability-reason`}
                            />

                            <ReactTooltip html={true} id={`tip-${uuid}-${certification.competence_profile.uuid}-availability-reason`} effect="solid" place="left">
                                {this.getAvailabilityReasonTooltipText(certification)}
                            </ReactTooltip>
                        </>
                    )}

                    {depth < 2 && this.renderCertificationNodes(certification.children, selectedCertification, uuid, true, depth + 1, false, showTooltip)}
                </div>
            ) : (
                this.renderCertificationNodes(certification.children, selectedCertification, uuid, true, depth + 1, true, showTooltip)
            );
        });
    };

    getAvailabilityReasonTooltipText = (certification) => {
        let text = "";

        if (certification.available) {
            text += `Deze certificering is beschikbaar door het behalen van:<br /><br />`;

            text += certification.end_terms.map((item) => `- ${item.name} op ${item.latest_progress?.date}`).join("<br />");
        } else {
            text += `Deze certificering is niet beschikbaar door het niet behalen van:<br /><br />`;

            text += certification.reasons
                ?.map((reason) => {
                    let reasonText = `<b>${reason.description}</b>`;
                    if ("end_terms" in reason) {
                        // this reason includes a list of end terms:
                        reasonText += "<br />" + reason.end_terms.map((endTerm) => `- ${endTerm.name}`).join("<br />");
                    }
                    return reasonText;
                })
                .join("<br />");
        }

        return text;
    };

    fetchCertifications = async () => {
        try {
            const response = await axios.get(`/v1/certifications/?course=${this.props.params?.itemID}`);

            this.setState({ certifications: response.data.results });
        } catch (error) {
            console.error("Error fetching certifications:", error);
        }
    };

    render() {
        const everyUserHasCertificate = this.state.students.every((user) => this.props.certificates.some((certificate) => certificate.user.uuid === user.uuid));
        const isNIBHVUser = hasStaffRole(this.props.authentication?.user?.roles || []);
        const isContact = isTrainerContact(this.props.authentication.user.roles || []);
        // const usersWithFirstLevelCertificate = [];

        // this.state.certifications.forEach((cert) => {
        //     if (cert.competence_profile?.uuid === this.props.course?.competence_profile) {
        //         const foundUser = this.state.students.find((st) => st.uuid === cert.user?.uuid);
        //         if (foundUser) usersWithFirstLevelCertificate.push(foundUser);
        //     }
        // });

        return (
            <div style={{ paddingBottom: "50px" }}>
                {this.state.loading ? (
                    <Loader />
                ) : (
                    <>
                        {this.state.isPolling && (
                            <div className="form-error alert alert-danger polling-warning" key="Polling error">
                                <span>{this.state.message}</span>
                                <div style={{ marginLeft: "10px" }} className="print-students-loader"></div>
                            </div>
                        )}
                        <div style={{ marginTop: "50px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h5>Vraag nieuwe certificeringen aan</h5>
                            <span>
                                <i className="fa fa-check" style={{ color: "#999", marginRight: "5px" }}></i>(deel)certificering al aangemaakt
                            </span>
                        </div>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>
                                        <div style={{ fontSize: "16px", fontWeight: "bold", marginLeft: "2.5px" }}>
                                            {this.state.students.filter((student) => student.checkedForSubmit).length}
                                        </div>
                                        <div>
                                            <i
                                                style={{ margin: 0, backgroundColor: !everyUserHasCertificate ? "#f60" : "#ccc" }}
                                                className="fa fa-check save-all-exemptions-icon"
                                                onClick={() => this.checkAllStudentsForField("checkedForSubmit")}
                                            />
                                        </div>
                                    </th>
                                    <th>NIBHV nummer</th>
                                    <th>Naam</th>
                                    <th>
                                        <div>
                                            <i
                                                style={{ margin: 0 }}
                                                className="fa fa-check save-all-exemptions-icon"
                                                onClick={() => {
                                                    const allChecked = this.state.students?.every((s) => s["passChecked"] === true);

                                                    const shouldShowWarning = this.state.students?.some((student) => {
                                                        const relatedOnly = filterUnrelatedCertifications(this.props.competenceProfile, student.available_certifications);
                                                        const tree = buildCertificationTree(relatedOnly);
                                                        const withoutSiblings = removeSiblingsOfProfile(this.props.competenceProfile, tree);
                                                        const highestCertification = this.findHighestCertification(withoutSiblings);

                                                        if (highestCertification[0]?.competence_profile.uuid !== this.props.competenceProfile?.uuid) return true;
                                                    });

                                                    if (shouldShowWarning && !allChecked) this.setState({ levelTwoPassWarningVisible: true });

                                                    this.checkAllStudentsForField("passChecked");
                                                }}
                                            />
                                            <span style={{ marginLeft: "7px" }}>
                                                Fysieke pas (<b>€</b>1,95)
                                            </span>
                                        </div>
                                    </th>
                                    {this.props.course.insurance_available && (
                                        <th>
                                            <div>
                                                <i
                                                    style={{ margin: 0 }}
                                                    className="fa fa-check save-all-exemptions-icon"
                                                    onClick={() => this.checkAllStudentsForField("selectedInsurance")}
                                                />
                                                <span style={{ marginLeft: "7px" }}>Verzekering</span> <i className="fa fa-shield" />
                                            </div>
                                        </th>
                                    )}
                                    {this.props.allowEmployerSpecific && (
                                        <th>
                                            <div>
                                                <i
                                                    style={{ margin: 0 }}
                                                    className="fa fa-check save-all-exemptions-icon"
                                                    onClick={() => this.checkAllStudentsForField("selectedEmployerSpecific")}
                                                />
                                                <span style={{ marginLeft: "7px" }}>Bedrijfsspecifiek</span> <i className="fa fa-building" />
                                            </div>
                                        </th>
                                    )}
                                    <th>Beschikbare certificering</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.students.map((student, index) => {
                                    const certificate = this.props.certificates.find((cert) => cert.user.uuid === student.uuid);
                                    const relatedOnly = filterUnrelatedCertifications(this.props.competenceProfile, student.available_certifications);
                                    const tree = buildCertificationTree(relatedOnly, true);
                                    const withoutSiblings = removeSiblingsOfProfile(this.props.competenceProfile, tree);

                                    const highestCertification = this.findHighestCertification(withoutSiblings);

                                    let errors = this.state?.submitErrors?.[index];

                                    return (
                                        <tr key={student.uuid}>
                                            <td>
                                                <input
                                                    onChange={(e) => this.handleChangeStudentField(e, student.uuid, "checkedForSubmit")}
                                                    checked={student.checkedForSubmit}
                                                    type="checkbox"
                                                    disabled={highestCertification.length > 1}
                                                    className="checkbox checkbox--no-label"
                                                />
                                            </td>
                                            <td>{student.nibhv_number}</td>
                                            <td>
                                                <span>{student.full_name}</span>
                                                {!!certificate && (
                                                    <>
                                                        <div style={{ fontSize: "12px" }}>
                                                            <i style={{ marginRight: "5px" }} className="fa fa-check" />
                                                            {certificate.competence_profile.certification_name || certificate.competence_profile.name}
                                                            {certificate.created ? ` aangemaakt op ${moment(certificate.created).format("DD-MM-YYYY")}` : null}
                                                        </div>
                                                    </>
                                                )}
                                            </td>
                                            <td>
                                                <input
                                                    onChange={(e) => {
                                                        if (highestCertification[0]?.competence_profile.uuid !== this.props.competenceProfile?.uuid && e.target.checked) {
                                                            this.setState({ levelTwoPassWarningVisible: true });
                                                        }

                                                        this.handleChangeStudentField(e, student.uuid, "passChecked");
                                                    }}
                                                    checked={student.passChecked}
                                                    type="checkbox"
                                                    className="checkbox checkbox--no-label"
                                                />
                                            </td>
                                            {this.props.course.insurance_available && (
                                                <td>
                                                    <input
                                                        onChange={(e) => {
                                                            this.handleChangeStudentField(e, student.uuid, "selectedInsurance");
                                                        }}
                                                        checked={student.selectedInsurance}
                                                        type="checkbox"
                                                        className="checkbox checkbox--no-label"
                                                    />
                                                </td>
                                            )}
                                            {this.props.allowEmployerSpecific && (
                                                <td>
                                                    <input
                                                        onChange={(e) => {
                                                            this.handleChangeStudentField(e, student.uuid, "selectedEmployerSpecific");
                                                        }}
                                                        checked={student.selectedEmployerSpecific}
                                                        type="checkbox"
                                                        className="checkbox checkbox--no-label"
                                                    />
                                                </td>
                                            )}

                                            <td>
                                                {errors && <FormErrorHandler errorObject={errors} fieldName="all" />}
                                                {this.renderCertificationNodes(withoutSiblings, student.selectedCertification, student.uuid)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </>
                )}
                <hr />
                <div style={{ float: "right" }}>
                    Aanmaak geschiedenis
                    <i
                        style={{ marginLeft: "15px", cursor: "pointer" }}
                        className={`fa fa-chevron-${this.state.certificationHistorySectionOpen ? "up" : "down"}`}
                        onClick={() => this.setState({ certificationHistorySectionOpen: !this.state.certificationHistorySectionOpen })}
                    />
                </div>
                {this.state.certificationHistorySectionOpen && (
                    <CertifyStudentsTrainerVersionHistorySection certifications={this.state.certifications} courseID={this.props.params?.itemID} />
                )}
                <div style={{ marginTop: "60px" }}>
                    <FormErrorHandler errorObject={this.state.error} fieldName="user" />
                    <FormErrorHandler errorObject={this.state.errorMessage} fieldName="all" />
                    <div className="flex flex-justify-end">
                        {/* Johan: 2024-04-17 - Added this setting to be able to disable this feature on production. APP_CONFIG is from app.config.js. */}
                        {APP_CONFIG.requestAdditionalPasses !== false && this.state.certifications.length > 0 && (isNIBHVUser || isContact) && (
                            <button
                                className="btn float-right btn-primary no-sideflap"
                                title="Er is momenteel al een lopende pasaanvraag voor de geselecteerde cursist(en)."
                                onClick={() => {
                                    this.setState({ passModalOpen: true });
                                }}
                            >
                                Vraag pas(sen) aan
                            </button>
                        )}
                        <button
                            disabled={this.state.isPolling || this.props.course.state === "finished"}
                            className="btn float-right btn-primary no-sideflap"
                            onClick={this.handleSubmit}
                        >
                            Certificeren
                        </button>
                    </div>
                </div>
                {this.state.submitting && (
                    <Dialog
                        title="De aangevraagde certificaten worden verwerkt. Het kan maximaal een uur duren voordat alle certificeringen beschikbaar zijn. Zodra de opdracht is afgerond ontvang je hierover een e-mail."
                        noClose={true}
                        visible={this.state.submitting}
                        animation="fade"
                    >
                        <Loader />
                    </Dialog>
                )}
                {this.state.passModalOpen && (
                    <PassRequestModal
                        visible={this.state.passModalOpen}
                        students={this.state.students}
                        certificates={this.props.certificates}
                        competenceProfile={this.props.competenceProfile}
                        renderCertificationNodes={this.renderCertificationNodes}
                        onClose={() => this.setState({ passModalOpen: false })}
                        setLevelTwoPassWarning={(val) => this.setState({ levelTwoPassWarningVisible: val })}
                        refetchData={() => {
                            this.fetchCertifications();
                            this.props.mutateCertificates();
                            this.props.courseDetailMutate();
                        }}
                    />
                )}
                {this.state.levelTwoPassWarningVisible && (
                    <Dialog
                        noClose
                        className="testmoments-date-warning-modal"
                        visible={this.state.levelTwoPassWarningVisible}
                        animation="fade"
                        footer={[
                            <button
                                className="btn btn-primary no-sideflap"
                                onClick={() => {
                                    this.setState({ levelTwoPassWarningVisible: false });
                                }}
                            >
                                Ik begrijp het
                            </button>
                        ]}
                    >
                        <p className="paragraph">
                            Let op: Je vraagt één of meerdere passen aan voor een deelmodule binnen de cursus. Volgt de cursist binnen 93 ook alle andere onderdelen
                            binnen dit competentieprofiel, dan raden we je aan om pas daarna een plastic pas aan te vragen. De cursist beschikt dan over één volledige pas
                            en er worden slechts eenmaal kosten in rekening gebracht.
                        </p>
                    </Dialog>
                )}
            </div>
        );
    }
}

export default CertifyStudentsTrainerVersion;
